import { zodResolver } from '@hookform/resolvers/zod';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  PriorityHigh as PriorityHighIcon,
  Close as CloseIcon,
  ViewList as ViewListIcon,
} from '@mui/icons-material';
import {
  Box,
  Stack,
  Button,
  Typography,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Skeleton,
  Tabs,
  Tab,
  Tooltip,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  itemInfrastructureDto,
  ItemInfrastructureDto,
  infrastructureType,
  infrastructureTypes,
  type InfrastructureRequirement,
  infrastructureLevel,
  infrastructureRelationship,
  stringifyPowerType,
} from '@nohara-dxdev/mt-common';
import { useState, useMemo, useEffect, useCallback } from 'react';
import {
  FormProvider,
  SelectElement,
  useForm,
  useFormContext,
  useFormState,
} from 'react-hook-form-mui';
import { useTranslation } from 'react-i18next';

import { NumberField } from '@/components';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { SelectItemModal } from '@/features/items/components/select-item-modal';
import { useInfrastructureItemMapping } from '@/features/items/hooks/use-infrastructure-item-mapping';
import { useInfrastructureRequirementsCheck } from '@/features/items/hooks/use-infrastructure-requirements-check';
import { ItemFilterValuesProvider, useItemFilterValues } from '@/features/items/providers';
import {
  buildInfrastructureRows,
  InfrastructureRow,
} from '@/features/items/utils/build-infrastructure-rows';
import { ItemEntity } from '@/types/api';

import { ManageSubItemsDialog } from '../manage-sub-items-dialog';

import { InfrastructureOfSubItemList } from './infrastructure-of-sub-item-list';
import { ItemFormData } from './types';

import type { GridFilterModel, GridLogicOperator } from '@mui/x-data-grid';

type RelationshipDirection = 'child' | 'parent';

interface ItemInfrastructureFormProps {
  requirements?: InfrastructureRequirement[];
}

export const ItemInfrastructureForm = ({ requirements = [] }: ItemInfrastructureFormProps) => {
  const { t } = useTranslation();
  const { watch, setValue, getValues, formState } = useFormContext<ItemFormData>();
  const [relationshipDirection, setRelationshipDirection] =
    useState<RelationshipDirection>('child');
  const [selectedInfraTab, setSelectedInfraTab] = useState(0);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isSelectItemModalOpen, setIsSelectItemModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [manageSubItemsInfrastructure, setManageSubItemsInfrastructure] = useState<{
    parentItemId: string;
    parentItemNumber: string;
    parentItemName: string;
    childItemId: string;
    childItemNumber: string;
    childItemName: string;
  } | null>(null);
  const currentInfrastructureType = infrastructureTypes[selectedInfraTab];

  const infrastructures = watch('infrastructures');
  const currentItemCircuits = watch('circuits');
  const currentItemId = getValues('id') ?? 'self';
  const infrastructureRelationshipConfigs = watch('infrastructureRelationshipConfigs');

  // Check infrastructure requirements
  const requirementsCheck = useInfrastructureRequirementsCheck({
    infrastructureRequirements: requirements,
    infrastructures,
    currentItemId,
  });

  const currentRequirement = useMemo<InfrastructureRequirement>(() => {
    return (
      requirements.find(
        (requirement) => requirement.infrastructureType === currentInfrastructureType,
      ) ?? {
        infrastructureType: currentInfrastructureType,
        relationshipLevel: infrastructureLevel.main,
        infrastructureRelationship: infrastructureRelationship.both,
      }
    );
  }, [currentInfrastructureType, requirements]);

  const currentConfigIndex = useMemo(() => {
    const index = infrastructureRelationshipConfigs.findIndex(
      (config) => config.infrastructureType === currentInfrastructureType,
    );
    return index >= 0 ? index : undefined;
  }, [infrastructureRelationshipConfigs, currentInfrastructureType]);

  const currentConfigLoad =
    currentConfigIndex !== undefined
      ? watch(`infrastructureRelationshipConfigs.${currentConfigIndex}.load`)
      : undefined;
  const currentConfigUnit =
    currentConfigIndex !== undefined
      ? watch(`infrastructureRelationshipConfigs.${currentConfigIndex}.unit`)
      : undefined;

  const currentTabInfrastructures = useMemo(() => {
    return infrastructures.filter((relationship) => {
      const matchesType = relationship.infrastructureType === currentInfrastructureType;
      if (relationshipDirection === 'child') {
        // Show items where current item is parent (childItemId points to other items)
        return matchesType && relationship.parentItemId === currentItemId;
      } else {
        // Show items where current item is child (parentItemId points to other items)
        return matchesType && relationship.childItemId === currentItemId;
      }
    });
  }, [infrastructures, currentInfrastructureType, currentItemId, relationshipDirection]);

  const addInfrastructureForm = useForm<ItemInfrastructureDto>({
    mode: 'onChange',
    resolver: zodResolver(itemInfrastructureDto),
  });
  // Must read isDirty during render, or RHF never subscribes and it stays stale.
  const { isDirty: isAddInfrastructureFormDirty } = useFormState({
    control: addInfrastructureForm.control,
  });

  // Temporary form for SelectItemModal (multi-select)
  const tempItemForm = useForm<{ tempSelectedItems: ItemEntity[] }>({
    defaultValues: { tempSelectedItems: [] },
  });

  const resetForm = useCallback(() => {
    const defaultValues =
      currentInfrastructureType === infrastructureType.electricity
        ? {
            parentItemId: relationshipDirection === 'child' ? currentItemId : '',
            childItemId: relationshipDirection === 'child' ? '' : currentItemId,
            circuitId: undefined,
            infrastructureType: currentInfrastructureType,
          }
        : {
            parentItemId: relationshipDirection === 'child' ? currentItemId : '',
            childItemId: relationshipDirection === 'child' ? '' : currentItemId,
            infrastructureType: currentInfrastructureType,
          };
    addInfrastructureForm.reset(defaultValues, {
      keepDefaultValues: false,
    });
  }, [addInfrastructureForm, currentInfrastructureType, relationshipDirection, currentItemId]);

  // Get unique item IDs based on relationship direction
  const relatedItemIds = useMemo(() => {
    if (relationshipDirection === 'child') {
      // For child mode, we need child item IDs
      return [
        ...new Set(currentTabInfrastructures.map((infra) => infra.childItemId).filter(Boolean)),
      ];
    } else {
      // For parent mode, we need parent item IDs
      return [
        ...new Set(currentTabInfrastructures.map((infra) => infra.parentItemId).filter(Boolean)),
      ];
    }
  }, [currentTabInfrastructures, relationshipDirection]);

  // Get parent filter values to filter out roomId/systemIds/buildingId/floorId
  const parentFilterValues = useItemFilterValues();

  // Filter out roomId, systemIds, buildingId, floorId from parent filterModel array
  const filteredFilterModel = useMemo(() => {
    const parentFilterModels = parentFilterValues.filterModel ?? [];
    if (parentFilterModels.length === 0) {
      return undefined;
    }

    // Filter each filterModel, removing items with excluded fields
    const filteredFilterModels = parentFilterModels.reduce<GridFilterModel[]>(
      (acc, filterModel) => {
        const filteredItems = filterModel.items.filter(
          (item) =>
            !['roomId', 'systemIds', 'buildingId', 'floorId', 'constructionTypeId'].includes(
              item.field,
            ),
        );

        // Return filterModel only if it has items after filtering
        if (filteredItems.length) {
          acc.push({
            items: filteredItems,
            logicOperator: filterModel.logicOperator ?? ('and' as GridLogicOperator),
          });
        }

        return acc;
      },
      [],
    );

    return filteredFilterModels.length > 0 ? filteredFilterModels : undefined;
  }, [parentFilterValues]);

  // Get items already added to prevent duplicates (for SelectItemModal filtering)
  const excludeIds = useMemo(() => {
    if (relationshipDirection === 'child') {
      // exclude items that are already added as parents to avoid circular references
      return [
        ...relatedItemIds,
        ...new Set(
          infrastructures
            .filter((infra) => infra.parentItemId !== currentItemId)
            .map((infra) => infra.parentItemId),
        ),
        ...(currentItemId !== 'self' ? [currentItemId] : []),
      ];
    } else {
      // exclude items that are already added as children to avoid circular references
      return [
        ...relatedItemIds,
        ...new Set(
          infrastructures
            .filter((infra) => infra.childItemId !== currentItemId)
            .map((infra) => infra.childItemId),
        ),
        ...(currentItemId !== 'self' ? [currentItemId] : []),
      ];
    }
  }, [relatedItemIds, currentItemId, infrastructures, relationshipDirection]);

  // Use the hook to efficiently map infrastructure item data
  // For parent mode, we need to fetch circuits from parent items
  const { getItem, isLoading: isLoadingItems } = useInfrastructureItemMapping({
    itemIds: relatedItemIds,
    enabled: relatedItemIds.length > 0 || formState.disabled,
    fetchCircuits:
      relationshipDirection === 'parent' &&
      currentInfrastructureType === infrastructureType.electricity,
  });

  const [parentItemId, childItemId, circuitId] = addInfrastructureForm.watch([
    'parentItemId',
    'childItemId',
    'circuitId',
  ]);
  // Watch for temp item selection (multi-select)
  const tempSelectedItems = tempItemForm.watch('tempSelectedItems');

  // Get available circuits based on relationship direction
  const availableCircuits = useMemo(() => {
    if (relationshipDirection === 'child') {
      // For child mode, use current item's circuits
      return currentItemCircuits;
    } else {
      // For parent mode (add): use circuits from single selected parent
      if (!isEditMode && tempSelectedItems.length === 1) {
        return tempSelectedItems[0].circuits ?? [];
      }
      // For parent mode (edit): use circuits from pre-populated parentItemId
      if (isEditMode && parentItemId && parentItemId !== 'self') {
        const parentItem = getItem(parentItemId);
        return parentItem?.circuits ?? [];
      }
      return [];
    }
  }, [
    relationshipDirection,
    currentItemCircuits,
    tempSelectedItems,
    isEditMode,
    parentItemId,
    getItem,
  ]);

  // Build circuit map from available circuits
  const circuitMap = useMemo(() => {
    return availableCircuits.reduce<
      Record<string, { id: string; name: string; formatted: string } | undefined>
    >((acc, circuit) => {
      if (circuit.id) {
        acc[circuit.id] = {
          id: circuit.id,
          name: circuit.name,
          formatted: stringifyPowerType(circuit),
        };
      }
      return acc;
    }, {});
  }, [availableCircuits]);

  const availableCircuitOptions = useMemo(() => {
    if (!availableCircuits.length) return [];
    return [
      {
        label: t('itemTemplates.detail.noChildItemCircuit'),
        id: '',
      },
      ...availableCircuits.map((circuit) => ({
        label: circuit.name,
        id: circuit.id,
      })),
    ];
  }, [availableCircuits, t]);

  // Clear stale circuitId when selected item or availableCircuits change
  useEffect(() => {
    if (circuitId && availableCircuits.length > 0) {
      const circuitExists = availableCircuits.some((circuit) => circuit.id === circuitId);
      if (!circuitExists) {
        addInfrastructureForm.setValue('circuitId', '', {
          shouldValidate: true,
        });
      }
    }
  }, [parentItemId, childItemId, availableCircuits, circuitId, addInfrastructureForm]);

  const handleClose = () => {
    setIsAddDialogOpen(false);
    setIsEditMode(false);
    resetForm();
    tempItemForm.reset({ tempSelectedItems: [] });
  };

  const handleAddDialogClose = (_event: unknown, reason: string) => {
    const hasUnsavedWork = tempSelectedItems.length > 0 || isAddInfrastructureFormDirty;
    if ((reason === 'backdropClick' || reason === 'escapeKeyDown') && hasUnsavedWork) {
      setDiscardConfirmOpen(true);
      return;
    }
    handleClose();
  };

  const handleCancelDiscard = () => {
    setDiscardConfirmOpen(false);
  };

  const handleConfirmDiscard = () => {
    setDiscardConfirmOpen(false);
    handleClose();
  };

  const handleAddItem = () => {
    if (!isEditMode && tempSelectedItems.length > 0) {
      // Multi-add mode: create one infrastructure entry per selected item
      const circuitId = addInfrastructureForm.getValues('circuitId');
      const newInfrastructures = [...infrastructures];

      for (const item of tempSelectedItems) {
        const cId = relationshipDirection === 'child' ? item.id : currentItemId;
        const pId = relationshipDirection === 'child' ? currentItemId : item.id;

        const existingIndex = newInfrastructures.findIndex(
          (infra) =>
            infra.childItemId === cId &&
            infra.parentItemId === pId &&
            infra.infrastructureType === currentInfrastructureType,
        );

        const newEntry = {
          childItemId: cId,
          parentItemId: pId,
          infrastructureType: currentInfrastructureType,
          ...(currentInfrastructureType === infrastructureType.electricity && circuitId
            ? { circuitId }
            : {}),
          isNew: true,
        } as ItemInfrastructureDto & { isNew: boolean };

        if (existingIndex !== -1) {
          newInfrastructures[existingIndex] = newEntry;
        } else {
          newInfrastructures.push(newEntry);
        }
      }

      setValue('infrastructures', newInfrastructures, { shouldDirty: true });
      handleClose();
    } else {
      // Single edit mode
      if (!addInfrastructureForm.formState.isValid) return;
      const data = addInfrastructureForm.getValues();
      const updatedInfrastructureIndex = infrastructures.findIndex(
        (infra) =>
          infra.childItemId === data.childItemId &&
          infra.parentItemId === data.parentItemId &&
          infra.infrastructureType === data.infrastructureType,
      );
      if (updatedInfrastructureIndex != -1) {
        infrastructures[updatedInfrastructureIndex] = data;
      } else {
        infrastructures.push({ ...data, isNew: true });
      }
      setValue('infrastructures', infrastructures, { shouldDirty: true });
      handleClose();
    }
  };

  const handleDeleteItem = (infrastructureKey: string) => {
    // Infrastructure key is the ID of the related item (child or parent depending on mode)
    const updatedInfrastructures = infrastructures.filter((infra) => {
      if (relationshipDirection === 'child') {
        return infra.childItemId !== infrastructureKey;
      } else {
        return infra.parentItemId !== infrastructureKey;
      }
    });
    setValue('infrastructures', updatedInfrastructures, { shouldDirty: true });
  };

  // Calculate Total Load from currently displayed items' infrastructure configs
  const totalLoad = useMemo(() => {
    let sum = 0;
    for (const infrastructure of currentTabInfrastructures) {
      const relatedItemId =
        relationshipDirection === 'child'
          ? infrastructure.childItemId
          : infrastructure.parentItemId;
      const item = getItem(relatedItemId);
      if (item) {
        // Find the infrastructure config for this infrastructure type in the related item
        const relatedItemConfig = item.infrastructureRelationshipConfigs?.find(
          (config) => config.infrastructureType === currentInfrastructureType,
        );
        if (relatedItemConfig?.load) {
          sum += Number(relatedItemConfig.load);
        }
      }
    }
    return sum;
  }, [currentTabInfrastructures, relationshipDirection, getItem, currentInfrastructureType]);

  // Calculate Ratio = (Design Load / Total Load) * 100%
  const loadRatio = useMemo(() => {
    if (!totalLoad || !currentConfigLoad) return 0;
    return (currentConfigLoad / totalLoad) * 100;
  }, [totalLoad, currentConfigLoad]);

  // Build combined circuit map for parent mode (circuits come from each parent item)
  const parentCircuitMap = useMemo(() => {
    if (
      relationshipDirection !== 'parent' ||
      currentInfrastructureType !== infrastructureType.electricity
    ) {
      return {} as Record<string, { id: string; name: string; formatted: string } | undefined>;
    }
    return relatedItemIds.reduce<
      Record<string, { id: string; name: string; formatted: string } | undefined>
    >((acc, id) => {
      const item = getItem(id);
      if (item?.circuits) {
        for (const circuit of item.circuits) {
          if (circuit.id) {
            acc[circuit.id] = {
              id: circuit.id,
              name: circuit.name,
              formatted: stringifyPowerType(circuit),
            };
          }
        }
      }
      return acc;
    }, {});
  }, [relationshipDirection, currentInfrastructureType, relatedItemIds, getItem]);

  // DataGrid rows: filter + map in one pure call
  const rows = useMemo(
    () =>
      buildInfrastructureRows({
        infrastructures,
        currentItemId,
        infrastructureType: currentInfrastructureType,
        direction: relationshipDirection,
        getItem,
        circuitMap: relationshipDirection === 'child' ? circuitMap : parentCircuitMap,
      }),
    [
      infrastructures,
      currentItemId,
      currentInfrastructureType,
      relationshipDirection,
      getItem,
      circuitMap,
      parentCircuitMap,
    ],
  );

  useEffect(() => {
    resetForm();
  }, [resetForm]);

  if (infrastructureTypes.length === 0) {
    return (
      <Box>
        <Typography variant="body2" color="textSecondary">
          {t('itemTemplates.detail.noInfrastructureTypes')}
        </Typography>
      </Box>
    );
  }

  // Check if current infrastructure type is a sub relationship
  const isSubRelationship = currentRequirement.relationshipLevel === infrastructureLevel.sub;

  // Electricity + parent mode only allows 1 item at a time
  const isElectricityParentMode =
    currentInfrastructureType === infrastructureType.electricity &&
    relationshipDirection === 'parent';

  return (
    <Box>
      {/* Relationship Type Indicator */}
      <Paper variant="elevation" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="subtitle2" fontWeight={600}>
            {t('infrastructure.relationshipType', 'Relationship Type')}:
          </Typography>
          <Chip
            label={
              isSubRelationship
                ? t('infrastructure.level.sub', 'Sub')
                : t('infrastructure.level.main', 'Main')
            }
            size="small"
            color={isSubRelationship ? 'default' : 'primary'}
            variant={isSubRelationship ? 'outlined' : 'filled'}
          />
        </Stack>
      </Paper>

      {/* Infrastructure Type Tabs */}
      <Stack direction="row" spacing={2} sx={{ mb: 2, alignItems: 'center' }}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Tabs
            value={selectedInfraTab}
            onChange={(_, newValue) => {
              setSelectedInfraTab(newValue);
            }}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              minHeight: 40,
              '& .MuiTabs-flexContainer': {
                gap: 1,
              },
              '& .MuiTab-root': {
                minHeight: 40,
                py: 1,
                px: 2,
                textTransform: 'none',
                fontSize: '0.875rem',
                fontWeight: 500,
              },
            }}
          >
            {infrastructureTypes.map((infrastructureType) => {
              const isMissingRequirement =
                requirementsCheck.isMissingRequirement(infrastructureType);
              return (
                <Tooltip
                  key={infrastructureType}
                  title={
                    isMissingRequirement
                      ? t(
                          'infrastructure.missingRequirementTooltip',
                          'This item requires at least one parent and one child item relationship.',
                        )
                      : ''
                  }
                  arrow
                >
                  <Tab
                    label={t(`infrastructure.${infrastructureType}`)}
                    disabled={formState.disabled}
                    sx={{
                      ...(isMissingRequirement && {
                        backgroundColor: 'error.light',
                        animation: 'pulse 1.2s infinite ease-in-out',

                        '@keyframes pulse': {
                          '0%': { backgroundColor: 'error.light' },
                          '50%': { backgroundColor: 'error.main' },
                          '100%': { backgroundColor: 'error.light' },
                        },

                        '&:hover': {
                          backgroundColor: 'error.main',
                        },
                      }),
                    }}
                  />
                </Tooltip>
              );
            })}
          </Tabs>
        </Box>
        {/* Hide Add button for sub relationships */}
        {!isSubRelationship && (
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => {
              setIsEditMode(false);
              tempItemForm.reset({ tempSelectedItems: [] });
              setIsAddDialogOpen(true);
            }}
            disabled={formState.disabled}
            sx={{ flexShrink: 0 }}
          >
            {t('common.add')}
          </Button>
        )}
      </Stack>

      {/* Show different content based on relationship level */}
      {isSubRelationship ? (
        // Sub relationship: Show read-only list
        <InfrastructureOfSubItemList infrastructureType={currentInfrastructureType} />
      ) : (
        // Main relationship: Show editable form
        <>
          {/* Child/Parent Relationship Direction Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs
              value={relationshipDirection}
              onChange={(_, newValue) => {
                setRelationshipDirection(newValue as RelationshipDirection);
              }}
            >
              <Tab
                label={t('infrastructure.relationshipDirection.child', 'Child')}
                value="child"
                disabled={formState.disabled}
              />
              <Tab
                label={t('infrastructure.relationshipDirection.parent', 'Parent')}
                value="parent"
                disabled={formState.disabled}
              />
            </Tabs>
          </Box>

          {/* Design Load, Unit, Total Load, and Ratio Section */}
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ xs: 'stretch', sm: 'flex-end' }}
            >
              <Box sx={{ minWidth: 150 }}>
                {/* have to use NumberField instead of NumberFieldFormElement because
                 there's a bug where if we switch tabs, the NumberFieldFormElement sets the value to undefined instead of keeping the value */}
                <NumberField
                  min={0}
                  label={t('infrastructure.designLoad', 'Design Load')}
                  value={currentConfigLoad}
                  error={
                    !!formState.errors.infrastructureRelationshipConfigs?.[currentConfigIndex ?? 0]
                      ?.load
                  }
                  helperText={
                    formState.errors.infrastructureRelationshipConfigs?.[currentConfigIndex ?? 0]
                      ?.load?.message
                  }
                  onValueCommitted={(value) => {
                    setValue(
                      `infrastructureRelationshipConfigs.${currentConfigIndex ?? 0}.load`,
                      value ?? 0,
                      {
                        shouldValidate: true,
                        shouldDirty: true,
                      },
                    );
                  }}
                  disabled={formState.disabled}
                />
              </Box>
              <Box sx={{ minWidth: 100 }}>
                {/* have to use TextField instead of TextFieldElement because
                 there's a bug where if we switch tabs, the NumberFieldFormElement sets the value to undefined instead of keeping the value */}
                <TextField
                  fullWidth
                  label={t('itemTemplates.detail.unit')}
                  placeholder="W"
                  value={currentConfigUnit ?? ''}
                  onChange={(e) => {
                    setValue(
                      `infrastructureRelationshipConfigs.${currentConfigIndex ?? 0}.unit`,
                      e.target.value,
                      {
                        shouldValidate: true,
                        shouldDirty: true,
                      },
                    );
                  }}
                  disabled={formState.disabled}
                />
              </Box>
              <Box sx={{ minWidth: 150 }}>
                <TextField
                  fullWidth
                  disabled
                  type="number"
                  label={t('infrastructure.totalLoad', 'Total Load')}
                  value={totalLoad.toFixed(0)}
                  slotProps={{
                    input: { readOnly: true },
                  }}
                />
              </Box>
              <Box sx={{ minWidth: 120 }}>
                <TextField
                  fullWidth
                  disabled
                  label={t('infrastructure.loadRatio', 'Rate')}
                  value={`${loadRatio.toFixed(1)}%`}
                  slotProps={{
                    input: { readOnly: true },
                  }}
                  sx={{
                    '& .MuiInputBase-input': {
                      color: loadRatio > 100 ? 'error.main' : 'primary.main',
                      fontWeight: 'bold',
                    },
                  }}
                />
              </Box>
            </Stack>
          </Paper>

          {/* Items DataGrid */}
          {(() => {
            const columns: GridColDef<InfrastructureRow>[] = [
              {
                field: 'itemNumber',
                headerName: t('items.list.columns.itemNumber'),
                flex: 1,
                minWidth: 100,
              },
              {
                field: 'name',
                headerName: t('items.list.columns.name'),
                flex: 2,
                minWidth: 150,
              },
              {
                field: 'room',
                headerName: t('reports.loadList.columns.room', 'Room'),
                flex: 2,
                minWidth: 150,
              },
              {
                field: 'circuitName',
                headerName: t('itemTemplates.detail.childCircuit'),
                flex: 1,
                minWidth: 100,
                renderCell: ({ row }: GridRenderCellParams<InfrastructureRow>) =>
                  row.circuitName || <PriorityHighIcon color="error" />,
              },
              {
                field: 'typeOfPower',
                headerName: t('itemTemplates.detail.typeOfPower'),
                flex: 1,
                minWidth: 100,
                renderCell: ({ row }: GridRenderCellParams<InfrastructureRow>) =>
                  row.typeOfPower || <PriorityHighIcon color="error" />,
              },
              {
                field: 'load',
                headerName: t('itemTemplates.detail.load'),
                type: 'number',
                flex: 1,
                minWidth: 80,
                valueFormatter: (value: number | null) => value ?? '-',
              },
              {
                field: 'unit',
                headerName: t('itemTemplates.detail.unit'),
                flex: 1,
                minWidth: 80,
              },
              {
                field: 'actions',
                headerName: t('common.actions'),
                sortable: false,
                filterable: false,
                width: 120,
                renderCell: ({ row }: GridRenderCellParams<InfrastructureRow>) => {
                  const { infrastructure } = row;
                  return (
                    <>
                      <IconButton
                        size="small"
                        color="primary"
                        disabled={formState.disabled || isLoadingItems}
                        onClick={() => {
                          addInfrastructureForm.reset({
                            parentItemId: infrastructure.parentItemId,
                            childItemId: infrastructure.childItemId,
                            circuitId:
                              'circuitId' in infrastructure ? infrastructure.circuitId : undefined,
                            infrastructureType: infrastructure.infrastructureType,
                          } as ItemInfrastructureDto);
                          tempItemForm.reset({ tempSelectedItems: [] });
                          setIsEditMode(true);
                          setIsAddDialogOpen(true);
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                      <Tooltip
                        title={
                          row.isNew
                            ? t(
                                'infrastructure.subItems.saveBeforeManage',
                                'Please save the form before managing sub-items',
                              )
                            : t('infrastructure.subItems.manageSubItems', 'Manage Sub-items')
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            color="info"
                            disabled={row.isNew || formState.disabled || isLoadingItems}
                            onClick={() => {
                              const parentItem =
                                relationshipDirection === 'child'
                                  ? {
                                      id: currentItemId,
                                      itemNumber: getValues('itemNumber'),
                                      name: getValues('name'),
                                    }
                                  : getItem(infrastructure.parentItemId);
                              const childItem =
                                relationshipDirection === 'child'
                                  ? getItem(infrastructure.childItemId)
                                  : {
                                      id: currentItemId,
                                      itemNumber: getValues('itemNumber'),
                                      name: getValues('name'),
                                    };
                              if (parentItem && childItem) {
                                setManageSubItemsInfrastructure({
                                  parentItemId: infrastructure.parentItemId,
                                  parentItemNumber: parentItem.itemNumber || '',
                                  parentItemName: parentItem.name || '',
                                  childItemId: infrastructure.childItemId,
                                  childItemNumber: childItem.itemNumber || '',
                                  childItemName: childItem.name || '',
                                });
                              }
                            }}
                          >
                            <ViewListIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <IconButton
                        size="small"
                        color="error"
                        disabled={formState.disabled || isLoadingItems}
                        onClick={() => {
                          handleDeleteItem(row.id);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </>
                  );
                },
              },
            ];

            return (
              <DataGrid
                rows={rows}
                columns={columns}
                loading={isLoadingItems}
                disableRowSelectionOnClick
                columnVisibilityModel={{
                  circuitName: currentInfrastructureType === infrastructureType.electricity,
                  typeOfPower: currentInfrastructureType === infrastructureType.electricity,
                }}
                sx={{ '--DataGrid-overlayHeight': '200px' }}
              />
            );
          })()}

          {/* Add Item Dialog */}
          <FormProvider {...addInfrastructureForm}>
            <Dialog open={isAddDialogOpen} onClose={handleAddDialogClose} maxWidth="sm" fullWidth>
              <DialogTitle>{t('itemTemplates.detail.addInfrastructureItem')}</DialogTitle>
              <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                  {/* Item Selection - label changes based on relationship direction */}
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      {relationshipDirection === 'child'
                        ? t('infrastructure.form.childItem', 'Child Item')
                        : t('infrastructure.form.parentItem', 'Parent Item')}{' '}
                      *
                    </Typography>
                    {isEditMode ? (
                      // Edit mode: show existing item chip from addInfrastructureForm
                      (() => {
                        const existingItemId =
                          relationshipDirection === 'child' ? childItemId : parentItemId;
                        const existingItem = getItem(existingItemId);
                        const isItemLoading = isLoadingItems && !existingItem;

                        if (isItemLoading) {
                          return <Skeleton width={300} height={40} />;
                        }

                        return (
                          <Chip
                            label={`${existingItem?.name ?? 'Unknown'} - ${existingItem?.itemNumber ?? 'N/A'}`}
                            deleteIcon={<CloseIcon />}
                            color="primary"
                            variant="filled"
                          />
                        );
                      })()
                    ) : (
                      // Add mode: show chips for each selected item + select button
                      <Stack spacing={1}>
                        {tempSelectedItems.length > 0 && (
                          <Stack direction="row" flexWrap="wrap" gap={1}>
                            {tempSelectedItems.map((item) => (
                              <Chip
                                key={item.id}
                                label={`${item.name || 'Unknown'} - ${item.itemNumber || 'N/A'}`}
                                onDelete={() => {
                                  tempItemForm.setValue(
                                    'tempSelectedItems',
                                    tempSelectedItems.filter((i) => i.id !== item.id),
                                  );
                                }}
                                deleteIcon={<CloseIcon />}
                                color="primary"
                                variant="filled"
                              />
                            ))}
                          </Stack>
                        )}
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            setIsSelectItemModalOpen(true);
                          }}
                          disabled={
                            formState.disabled ||
                            (isElectricityParentMode && tempSelectedItems.length >= 1)
                          }
                          sx={{ alignSelf: 'flex-start' }}
                        >
                          {relationshipDirection === 'child'
                            ? t('infrastructure.form.selectChildItem', 'Select Child Item')
                            : t('infrastructure.form.selectParentItem', 'Select Parent Item')}
                        </Button>
                      </Stack>
                    )}
                  </Box>

                  {/* Circuit and Type of Power Row - shown for electricity */}
                  {currentInfrastructureType === infrastructureType.electricity && (
                    <Stack direction="row" spacing={2}>
                      <SelectElement
                        fullWidth
                        disabled={!availableCircuitOptions.length || formState.disabled}
                        options={availableCircuitOptions}
                        label={t('itemTemplates.detail.childItemCircuit')}
                        name="circuitId"
                      />
                      <TextField
                        fullWidth
                        disabled
                        label={t('itemTemplates.detail.typeOfPower')}
                        value={circuitId ? (circuitMap[circuitId]?.formatted ?? '') : ''}
                      />
                    </Stack>
                  )}
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleClose}>{t('common.cancel')}</Button>
                <Button
                  onClick={handleAddItem}
                  variant="contained"
                  disabled={
                    formState.disabled ||
                    (isEditMode
                      ? !addInfrastructureForm.formState.isValid
                      : tempSelectedItems.length === 0)
                  }
                >
                  {t('common.save')}
                </Button>
              </DialogActions>
            </Dialog>
          </FormProvider>

          {/* Select Item Modal - Wrapped with filter provider to exclude already added items */}
          <ItemFilterValuesProvider
            filterValues={{
              // Filter out roomId, systemIds, buildingId, floorId from parent context
              // We filter the parent filterModel to exclude these fields
              // and set locationIdOrAdjacentLocationIdEq to undefined to clear it
              filterModel: filteredFilterModel,
              excludeIds,
              locationIdOrAdjacentLocationIdEq: undefined, // Explicitly clear location filter
              infrastructureRelationshipRequirements: [
                `${currentInfrastructureType}|${infrastructureLevel.main}`,
              ],
            }}
          >
            <FormProvider {...tempItemForm}>
              <SelectItemModal
                name="tempSelectedItems"
                open={isSelectItemModalOpen}
                onClose={() => {
                  setIsSelectItemModalOpen(false);
                }}
                multiValue={true}
                maxItems={isElectricityParentMode ? 1 : undefined}
                title={
                  relationshipDirection === 'child'
                    ? t('infrastructure.form.selectChildItem', 'Select Child Item')
                    : t('infrastructure.form.selectParentItem', 'Select Parent Item')
                }
              />
            </FormProvider>
          </ItemFilterValuesProvider>

          {/* Manage Sub-items Dialog */}
          {manageSubItemsInfrastructure && (
            <ManageSubItemsDialog
              open={!!manageSubItemsInfrastructure}
              onClose={() => {
                setManageSubItemsInfrastructure(null);
              }}
              parentItemId={manageSubItemsInfrastructure.parentItemId}
              parentItemNumber={manageSubItemsInfrastructure.parentItemNumber}
              parentItemName={manageSubItemsInfrastructure.parentItemName}
              childItemId={manageSubItemsInfrastructure.childItemId}
              childItemNumber={manageSubItemsInfrastructure.childItemNumber}
              childItemName={manageSubItemsInfrastructure.childItemName}
              infrastructureType={currentInfrastructureType}
            />
          )}
        </>
      )}

      <ConfirmDialog
        open={discardConfirmOpen}
        onClose={handleCancelDiscard}
        onConfirm={handleConfirmDiscard}
        title={t('items.edit.discardTitle', 'Discard unsaved changes?')}
        message={t(
          'items.edit.discardMessage',
          'You have unsaved changes. Do you want to discard them and close?',
        )}
        confirmText={t('items.edit.discardConfirm', 'Discard')}
        cancelText={t('items.edit.discardCancel', 'Keep editing')}
      />
    </Box>
  );
};
